// =============================================================================
// src/utils/leadStatus.ts
// Single source of truth for lead status values + their visual styling. Shared
// by the dashboard leads list, the landing showcase and (indirectly) the API
// layer, so what a visitor sees on the landing matches the real product.
// No Supabase imports here — safe to pull into the public bundle.
// =============================================================================

// Aligned with the deal/contact pipeline (`deals.stage` / derived contact stage)
// so the three surfaces share one vocabulary: new → contacted → qualified → won/lost.
import { APP_LANGUAGES, type Language } from "@/types/common.types";

export type LeadStatus = "new" | "contacted" | "qualified" | "won" | "lost";

export const LEAD_STATUSES: LeadStatus[] = [
  "new",
  "contacted",
  "qualified",
  "won",
  "lost",
];

// Preferred contact language a lead may pick on the viewer / demo forms.
// Derived from APP_LANGUAGES, not a list of its own: the form used to offer
// only es/pt/en while the app (and every transactional email) shipped 5, so a
// French or Italian visitor had no way to say which language to answer in and
// the field arrived empty.
export type LeadLanguage = Language;

export const LEAD_LANGUAGES: LeadLanguage[] = APP_LANGUAGES.map((l) => l.code);

// Coerce an i18n locale (e.g. "pt-BR") to a LeadLanguage, or null if unsupported.
export function toLeadLanguage(locale: string | undefined): LeadLanguage | null {
  if (!locale) return null;
  return LEAD_LANGUAGES.find((l) => locale.startsWith(l)) ?? null;
}

// Pill styling per status (chip background + text + border).
export const STATUS_STYLES: Record<LeadStatus, string> = {
  new: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
  contacted: "bg-amber-500/10 text-amber-800 dark:text-amber-400 border-amber-500/30",
  qualified: "bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/30",
  won: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  lost: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30",
};

// Solid dot used in the status dropdown menu.
export const STATUS_DOT: Record<LeadStatus, string> = {
  new: "bg-blue-500",
  contacted: "bg-amber-500",
  qualified: "bg-violet-500",
  won: "bg-emerald-500",
  lost: "bg-rose-500",
};

// Solid hex per status for the conic donuts (SVG stroke needs hex, not the
// tailwind tint classes). Shared by /dashboard/leads and the Negocio section
// of /dashboard/analytics so both read the same colors.
export const LEAD_STATUS_COLOR: Record<LeadStatus, string> = {
  new: "#3b82f6",
  contacted: "#f59e0b",
  qualified: "#8b5cf6",
  won: "#10b981",
  lost: "#f43f5e",
};

// Localized relative time ("3h ago" / "yesterday") via the native Intl API.
// Compact, unit-only relative time for tight mobile rows: "30m" / "5h" / "2d".
export function relativeTimeShort(iso: string, lang: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `${Math.max(min, 0)}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString(lang);
}

export function relativeTime(iso: string, lang: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  if (min < 1) return rtf.format(0, "minute");
  if (min < 60) return rtf.format(-min, "minute");
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(-hr, "hour");
  const days = Math.round(hr / 24);
  if (days < 30) return rtf.format(-days, "day");
  return new Date(iso).toLocaleDateString(lang);
}
